<template>
    <div class="module">
        <div v-if="loading" class="loading">Chargement...</div>

        <template v-else-if="event">
            <h2 class="page-title">Inscrits {{ event.full_name || event.display_name || event.name }}</h2>

            <div class="event-banner">
                <p v-if="event.event_date"><span class="icon">📅</span> {{ formatEventDateRange(event.event_date, event.event_date_end) }}</p>
                <p v-if="event.location"><span class="icon">📍</span> {{ event.location }}</p>
                <p v-if="event.club_name"><span class="icon">🏆</span> {{ event.club_name }}</p>
            </div>

            <div v-if="isLinkedToBadarts ? !badartsPlayers.length : !rows.length" class="no-data">
                Aucun inscrit pour le moment.
            </div>

            <template v-else>
                <LoginPrompt v-if="!isAuthenticated"
                    message="Connectez-vous pour rechercher un joueur, filtrer par catégorie ou par club et voir les clubs des inscrits." />

                <!-- Totaux sur joueurs uniques (un joueur inscrit en simple et en double compte
                une fois), après recherche et filtres ligue / comité / club, comme sur l'ancienne
                liste de CMER. Licenciés / non licenciés : officiel seulement. -->
                <div v-if="isAuthenticated && !isLinkedToBadarts" class="roster-totals">
                    <div class="total-group grand-total">
                        <h3>Total</h3>
                        <div class="total-item big"><span>Joueurs</span> <strong>{{ totals.players }}</strong></div>
                        <template v-if="!isLocal">
                            <div class="total-item"><span>Licenciés</span> <strong>{{ totals.licensed }}</strong></div>
                            <div class="total-item"><span>Non licenciés</span> <strong>{{ totals.nonLicensed }}</strong></div>
                        </template>
                    </div>
                    <div v-for="cat in totals.categories" class="total-group" :key="cat.key">
                        <h3>{{ cat.label }}</h3>
                        <template v-if="!isLocal">
                            <div class="total-item"><span>Licenciés</span> <strong>{{ cat.licensed }}</strong></div>
                            <div class="total-item"><span>Non licenciés</span> <strong>{{ cat.nonLicensed }}</strong></div>
                        </template>
                        <div v-else class="total-item"><span>Joueurs</span> <strong>{{ cat.licensed + cat.nonLicensed }}</strong></div>
                    </div>
                </div>

                <div v-if="isAuthenticated" class="roster-toolbar">
                    <input v-model="search" type="text" class="roster-search" placeholder="Rechercher un joueur..." />
                    <template v-if="!isLinkedToBadarts">
                        <label v-if="uniqueLigues.length" class="roster-filter">Ligue :
                            <select v-model="selectedLigue">
                                <option value="">Toutes</option>
                                <option v-for="ligue in uniqueLigues" :value="ligue" :key="ligue">
                                    {{ ligueNames[ligue] || ligue }}
                                </option>
                            </select>
                        </label>
                        <label v-if="uniqueComites.length" class="roster-filter">Comité :
                            <select v-model="selectedComite">
                                <option value="">Tous</option>
                                <option v-for="comite in filteredComites" :value="comite" :key="comite">
                                    {{ comiteInfo[comite]?.name || comite }}
                                </option>
                            </select>
                        </label>
                        <label v-if="!isLocal" class="roster-filter">Club :
                            <select v-model="selectedClub" :disabled="!uniqueClubs.length">
                                <option value="">Tous</option>
                                <option v-for="club in uniqueClubs" :value="club" :key="club">{{ club }}</option>
                            </select>
                        </label>
                        <label class="roster-filter">Catégorie :
                            <select v-model="selectedCategory">
                                <option value="">Toutes</option>
                                <option v-for="group in groupedRows" :value="group.key" :key="group.key">
                                    {{ group.label }}
                                </option>
                            </select>
                        </label>
                    </template>
                    <label v-if="!isLinkedToBadarts" class="roster-mine-toggle">
                        <input v-model="onlyMine" type="checkbox" />
                        Mon inscription
                    </label>
                </div>

                <div v-if="!filteredGroups.length" class="no-data">
                    Aucun joueur ne correspond aux filtres.
                </div>

                <div v-for="group in filteredGroups" class="category-block" :key="group.key">
                    <h3 class="category-title">{{ group.label }} <span v-if="isAuthenticated" class="category-count">({{ group.rows.length }})</span></h3>
                    <div class="roster-list">
                        <div v-for="row in group.rows" class="roster-row" :class="{ 'roster-row--mine': row.isMine }" :key="row.id">
                            <span class="roster-name">
                                {{ titleCase(row.firstname) }} {{ titleCase(row.name) }}<template v-if="isAuthenticated && row.code"> ({{ row.code }})</template><template v-if="row.partnerName"> / {{ titleCase(row.partnerName) }}<template v-if="isAuthenticated && row.partnerCode"> ({{ row.partnerCode }})</template></template>
                            </span>
                            <span v-if="row.seekingPartner" class="roster-seeking">Cherche doublette</span>
                        </div>
                    </div>
                </div>
            </template>

            <RosterDuplicates v-if="isAdmin && !isLinkedToBadarts" :eventId="event.id" @merged="load" />
        </template>

        <div v-else class="no-events">
            <p>Événement introuvable.</p>
            <router-link v-if="!isHostEmbed" to="/"><button>Retour au calendrier</button></router-link>
        </div>
    </div>
</template>

<script setup lang="ts">
import { parseEventId } from '@/functions/eventDisplay';
import { ref, computed, watch } from 'vue'
import { useRoute } from 'vue-router'
import backendApi from '@/axios/backendApi'
import { formatEventDateRange, titleCase, normalize, orderPair } from '@/functions/utils'
import { useAuthStore } from '@/stores/useAuthStore'
import { fetchComites, fetchLigues } from '@/functions/leaderboards'
import LoginPrompt from '@/components/LoginPrompt.vue'
import RosterDuplicates from '@/components/RosterDuplicates.vue'
import { isHostEmbed } from '@/embedHost'

const route = useRoute()
const authStore = useAuthStore()
// Visiteur anonyme : liste brute uniquement (ni recherche, ni filtres, ni compteurs, ni
// code club/comité entre parenthèses) + encart invitant à se connecter. Filtrage
// d'affichage seulement : /inscriptions/active reste public et inchangé (consommé
// anonymement par CMER et par le backend Badarts).
const isAuthenticated = computed(() => authStore.isAuthenticated)
const isAdmin = computed(() => authStore.scopes.some(s => s === 'super_admin' || s === 'badarts_administrator'))
const eventId = computed(() => parseEventId(route.params.eventId))
const search = ref('')
const onlyMine = ref(false)

const SIMPLE_CATEGORIES: Record<string, string> = { M: 'Individuel Masculin', V: 'Individuel Vétéran', F: 'Individuel Féminin', J: 'Individuel Junior' }
const DOUBLE_CATEGORIES: Record<string, string> = { DM: 'Double', DF: 'Double Féminin' }

const event = ref<any>(null)
const loading = ref(false)
// Un tournoi local ne vérifie aucune licence : la colonne club / "Non licencié" n'y
// apporte rien, on ne l'affiche que pour un officiel.
const isLocal = computed(() => event.value?.event_type === 'local')
interface Inscription {
    // Code entre parenthèses selon le niveau de l'événement : club (comité), comité
    // (ligue / fédéral) ou "NL" -- calculé côté serveur, cf.
    // Dartshub/backend/modules/api/inscriptions/club_display.py. Vide pour un local.
    display_code?: string | null
    id: number
    event_id: number
    name: string
    firstname: string
    club: string
    category_simple: string | null
    category_double: string | null
    doublette: number | null
}
const rows = ref<Inscription[]>([])

// Event lié à un tournoi Badarts (cf. EventInscriptions.vue::isLinkedToBadarts) : les
// inscrits vivent sur Badarts, pas dans Inscription (toujours vide pour ces events) --
// source différente, mêmes groupedRows/filteredGroups pour réutiliser tel quel le reste
// de la page (recherche, affichage).
const isLinkedToBadarts = computed(() => !!event.value?.linked_badarts_tournament_id)
const badartsPlayers = ref<{ id: number; firstname: string; name: string; nickname: string; participant_id: number | null }[]>([])
const badartsMode = ref<'single' | 'double' | null>(null)

// Page en lecture seule, ouverte à tous (version réduite pour un anonyme, cf.
// isAuthenticated) -- pas de contrôle premium ni de droit de gestion, contrairement à
// EventInscriptions.vue : équivalent du bouton "Voir les inscriptions" de CMER
// (OfficialCalendar.vue). /inscriptions/active est public côté backend.
const load = async () => {
    loading.value = true
    try {
        const [{ data: eventData }, { data: insData }] = await Promise.all([
            backendApi.get(`/events/${eventId.value}`),
            backendApi.get('/inscriptions/active', { params: { event_id: eventId.value } }),
        ])
        event.value = eventData
        rows.value = insData
        if (eventData?.linked_badarts_tournament_id) {
            const { data: badartsData } = await backendApi.get(`/events/${eventId.value}/badarts-roster/public`)
            badartsPlayers.value = badartsData.players ?? []
            badartsMode.value = badartsData.mode ?? null
        } else {
            badartsPlayers.value = []
            badartsMode.value = null
        }
    } catch {
        event.value = null
        rows.value = []
        badartsPlayers.value = []
    } finally {
        loading.value = false
    }
}

watch(eventId, load, { immediate: true })

interface RosterRow {
    id: number
    name: string
    firstname: string
    club: string
    code: string
    partnerName: string | null
    partnerCode: string
    // Clubs des deux joueurs d'une doublette (un seul en individuel), pour les filtres.
    clubs: string[]
    isMine: boolean
    // Inscrit en double sans partenaire (encore) attribué.
    seekingPartner: boolean
}

const groupedRows = computed(() => {
    const byId = new Map(rows.value.map(r => [r.id, r]))
    const groups: { key: string; label: string; rows: RosterRow[] }[] = []

    const pushRow = (key: string, label: string, registrant: Inscription, other: Inscription | null) => {
        let group = groups.find(g => g.key === key)
        if (!group) {
            group = { key, label, rows: [] }
            groups.push(group)
        }
        // Doublette : nom puis prénom par ordre alphabétique (cf. orderPair), quel que
        // soit celui des deux qui a fait l'inscription.
        const [ins, partner] = other ? orderPair(registrant, other) : [registrant, null]
        group.rows.push({
            id: ins.id, name: ins.name, firstname: ins.firstname, club: ins.club, code: ins.display_code || '',
            partnerName: partner ? `${partner.firstname} ${partner.name}` : null,
            partnerCode: partner?.display_code || '',
            clubs: partner ? [ins.club, partner.club] : [ins.club],
            seekingPartner: key.startsWith('double-') && !partner,
            isMine: authStore.isCurrentUser_inscription(ins) || (!!partner && authStore.isCurrentUser_inscription(partner)),
        })
    }

    // Une doublette apparaît deux fois dans rows.value (une ligne par joueur, chacune
    // pointant l'autre via .doublette) : sans déduplication par paire, chaque catégorie
    // double affichait le même couple deux fois (une fois dans chaque sens). Le
    // partenaire n'a aussi de sens que pour une catégorie double -- une catégorie
    // individuelle ne doit jamais l'afficher, même si le joueur a par ailleurs une
    // doublette sur cet événement.
    const seenPairs = new Set<string>()

    for (const ins of rows.value) {
        for (const cat of (ins.category_simple || '').split(',').map(c => c.trim()).filter(Boolean)) {
            pushRow(`simple-${cat}`, SIMPLE_CATEGORIES[cat] || cat, ins, null)
        }
        for (const cat of (ins.category_double || '').split(',').map(c => c.trim()).filter(Boolean)) {
            const partner = ins.doublette ? byId.get(ins.doublette) : null
            const pairKey = `${cat}-${partner ? [ins.id, partner.id].sort((a, b) => a - b).join('-') : `solo-${ins.id}`}`
            if (seenPairs.has(pairKey)) continue
            seenPairs.add(pairKey)
            pushRow(`double-${cat}`, DOUBLE_CATEGORIES[cat] || cat, ins, partner ?? null)
        }
    }

    return groups.sort((a, b) => a.label.localeCompare(b.label))
})

// Doublette Badarts : deux inscrits qui partagent le même participant_id (cf. même
// logique que EventInscriptions.vue::badartsPairs) -- pas de "isMine" (email non exposé
// publiquement, cf. events/routes.py::get_badarts_roster_public), pas de club (jamais
// connu côté Badarts, cf. Inscription plus haut).
const badartsGroupedRows = computed(() => {
    if (!badartsPlayers.value.length) return []
    const isDouble = badartsMode.value === 'double'
    const byParticipant = new Map<number, typeof badartsPlayers.value>()
    for (const p of badartsPlayers.value) {
        if (p.participant_id == null) continue
        if (!byParticipant.has(p.participant_id)) byParticipant.set(p.participant_id, [])
        byParticipant.get(p.participant_id)!.push(p)
    }
    const rows: RosterRow[] = isDouble
        ? [...byParticipant.values()].filter(g => g.length === 2).map(([a, b]) => {
            // Tri sur le nom affiché (pseudo à défaut de nom), cf. orderPair.
            const [p1, p2] = orderPair({ ...a, name: a.name || a.nickname }, { ...b, name: b.name || b.nickname })
            return {
                id: p1.id, name: p1.name, firstname: p1.firstname, club: '', code: '',
                partnerName: `${p2.firstname} ${p2.name}`, partnerCode: '', clubs: [], isMine: false, seekingPartner: false,
            }
        })
        : badartsPlayers.value.map(p => ({
            id: p.id, name: p.name || p.nickname, firstname: p.firstname, club: '', code: '', partnerName: null, partnerCode: '', clubs: [], isMine: false, seekingPartner: false,
        }))
    return [{ key: 'badarts', label: isDouble ? 'Doublettes' : 'Individuels', rows }]
})

// Filtres ligue / comité / club / catégorie, alignés sur la liste des inscrits de CMER
// (pages/user/Inscriptions.vue). Ligue et comité se déduisent du champ club
// ("Club (MER)" | "MER" | "Club") ; les noms viennent du référentiel DartsHub.
const selectedLigue = ref('')
const selectedComite = ref('')
const selectedClub = ref('')
const selectedCategory = ref('')

const ligueNames = ref<Record<string, string>>({})
const comiteInfo = ref<Record<string, { name: string; ligue: string | null }>>({})
Promise.all([fetchLigues(), fetchComites()]).then(([ligues, comites]) => {
    ligueNames.value = Object.fromEntries(ligues.map(l => [l.code, l.name]))
    comiteInfo.value = Object.fromEntries(comites.map(c => [c.code, { name: c.name, ligue: c.ligue_code ?? null }]))
}).catch(() => { /* silencieux -- les filtres affichent alors les codes bruts */ })

const extractClubInfo = (club: string): { clubName: string; comite: string } => {
    const match = (club || '').match(/^(.+?)\s*\(([^)]+)\)\s*$/)
    if (match) return { clubName: match[1].trim(), comite: match[2].trim() }
    if (/^[A-Z0-9]{2,10}$/.test((club || '').trim())) return { clubName: '', comite: club.trim() }
    return { clubName: club || '', comite: '' }
}
const ligueOfComite = (comite: string) => comiteInfo.value[comite]?.ligue ?? ''

const uniqueComites = computed(() => {
    const s = new Set<string>()
    for (const i of rows.value) { const { comite } = extractClubInfo(i.club); if (comite) s.add(comite) }
    return [...s].sort()
})
const uniqueLigues = computed(() =>
    [...new Set(uniqueComites.value.map(ligueOfComite).filter(Boolean))].sort()
)
const filteredComites = computed(() =>
    selectedLigue.value ? uniqueComites.value.filter(c => ligueOfComite(c) === selectedLigue.value) : uniqueComites.value
)
// Clubs restreints à la ligue / au comité sélectionnés ; un club sans comité connu
// n'apparaît que sans filtre ligue/comité.
const uniqueClubs = computed(() => {
    const s = new Set<string>()
    for (const i of rows.value) {
        const { clubName, comite } = extractClubInfo(i.club)
        if (!clubName || ['Non licencié', 'Non renseigné', 'Licencié', 'FFD'].includes(clubName)) continue
        if (selectedComite.value && comite !== selectedComite.value) continue
        if (selectedLigue.value && ligueOfComite(comite) !== selectedLigue.value) continue
        s.add(clubName)
    }
    return [...s].sort()
})
watch(selectedLigue, () => {
    if (selectedComite.value && !filteredComites.value.includes(selectedComite.value)) selectedComite.value = ''
})
// Déconnexion : filtres masqués pour un anonyme, ils ne doivent plus s'appliquer.
watch(isAuthenticated, (auth) => {
    if (auth) return
    search.value = ''
    onlyMine.value = false
    selectedLigue.value = selectedComite.value = selectedClub.value = selectedCategory.value = ''
})
watch(uniqueClubs, (clubs) => {
    if (selectedClub.value && !clubs.includes(selectedClub.value)) selectedClub.value = ''
})

const clubMatches = (club: string): boolean => {
    const { clubName, comite } = extractClubInfo(club)
    if (selectedLigue.value && ligueOfComite(comite) !== selectedLigue.value) return false
    if (selectedComite.value && comite !== selectedComite.value) return false
    if (selectedClub.value && clubName !== selectedClub.value) return false
    return true
}

// Totaux sur joueurs uniques (clé NOM Prénom), mêmes règles que l'ancienne liste de CMER :
// recherche + filtres ligue / comité / club / "Mon inscription" appliqués, pas le filtre
// catégorie ; un double ne compte que les joueurs qui ont un partenaire.
const TOTAL_CATEGORIES: { key: string; label: string }[] = [
    { key: 'M', label: 'Masculins' }, { key: 'V', label: 'Vétérans' }, { key: 'F', label: 'Féminines' },
    { key: 'J', label: 'Juniors' }, { key: 'DM', label: 'Double' }, { key: 'DF', label: 'Double féminin' },
]
const splitCats = (cats: string | null) => (cats || '').split(',').map(c => c.trim()).filter(Boolean)
const isNonLicensed = (ins: Inscription) =>
    (ins.display_code || '').toUpperCase() === 'NL' || ['nl', 'nonlic', 'non licencié'].includes((ins.club || '').trim().toLowerCase())

const totals = computed(() => {
    const query = normalize(search.value.trim())
    const list = rows.value.filter(ins =>
        clubMatches(ins.club)
        && (!onlyMine.value || authStore.isCurrentUser_inscription(ins))
        && (!query || normalize(`${ins.firstname} ${ins.name}`).includes(query) || normalize(`${ins.name} ${ins.firstname}`).includes(query)))
    const players = new Map<string, Inscription[]>()
    for (const ins of list) {
        const key = `${ins.name.trim().toUpperCase()} ${ins.firstname.trim().toUpperCase()}`
        players.set(key, [...(players.get(key) ?? []), ins])
    }
    const byCat: Record<string, { licensed: number; nonLicensed: number }> =
        Object.fromEntries(TOTAL_CATEGORIES.map(c => [c.key, { licensed: 0, nonLicensed: 0 }]))
    let licensed = 0
    for (const inscriptions of players.values()) {
        const isLicensed = !isNonLicensed(inscriptions[0])
        if (isLicensed) licensed++
        const field = isLicensed ? 'licensed' : 'nonLicensed'
        const paired = inscriptions.find(i => i.category_double && i.doublette)
        const cats = new Set([...inscriptions.flatMap(i => splitCats(i.category_simple)), ...splitCats(paired?.category_double ?? null)])
        for (const cat of cats) if (byCat[cat]) byCat[cat][field]++
    }
    return {
        players: players.size,
        licensed,
        nonLicensed: players.size - licensed,
        categories: TOTAL_CATEGORIES
            .map(c => ({ ...c, ...byCat[c.key] }))
            .filter(c => c.licensed + c.nonLicensed > 0),
    }
})

// "Mon inscription" cochée : ne garde QUE mes lignes (filtre), en plus de la
// recherche texte. Sans rapport avec la mise en évidence jaune (.roster-row--mine,
// cf. template), qui reste toujours affichée pour ma propre inscription.
const filteredGroups = computed(() => {
    const query = normalize(search.value.trim())
    const source = isLinkedToBadarts.value ? badartsGroupedRows.value : groupedRows.value
    return source
        .filter(group => !selectedCategory.value || group.key === selectedCategory.value)
        .map(group => ({
            ...group,
            rows: group.rows.filter(row => {
                if (onlyMine.value && !row.isMine) return false
                // Doublette : retenue si l'un des deux joueurs passe les filtres.
                if (row.clubs.length && !row.clubs.some(clubMatches)) return false
                if (!query) return true
                return normalize(`${row.firstname} ${row.name}`).includes(query) ||
                    (row.partnerName && normalize(row.partnerName).includes(query))
            }),
        }))
        .filter(group => group.rows.length)
})
</script>

<style scoped>
.event-banner {
    background: var(--color-accent-bis, #1a3a5c);
    color: var(--color-bg, #fff);
    padding: 1rem 1.5rem;
    border-radius: var(--radius, 8px);
    margin-bottom: 1.5rem;
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem 1.5rem;
    align-items: center;
}

.event-banner p {
    margin: 0;
    font-size: 0.95rem;
    display: flex;
    align-items: center;
    gap: 0.4rem;
}

.roster-toolbar {
    display: flex;
    flex-wrap: wrap;
    gap: 0.75rem 1.5rem;
    align-items: center;
    margin-bottom: 1.25rem;
}

.roster-search {
    flex: 1 1 220px;
    padding: 0.5rem 0.75rem;
    border: 1px solid var(--color-light-shadow, #ddd);
    border-radius: var(--radius, 8px);
    font-size: 0.9rem;
}

.roster-mine-toggle {
    display: flex;
    align-items: center;
    gap: 0.4rem;
    font-size: 0.9rem;
    cursor: pointer;
    white-space: nowrap;
}

.roster-filter {
    display: flex;
    align-items: center;
    gap: 0.4rem;
    font-size: 0.9rem;
    white-space: nowrap;
}

.roster-filter select {
    padding: 0.4rem 0.5rem;
    border: 1px solid var(--color-light-shadow, #ddd);
    border-radius: var(--radius, 8px);
    font-size: 0.9rem;
    max-width: 220px;
}

.roster-mine-toggle input {
    cursor: pointer;
}

.category-block {
    margin-bottom: 1.5rem;
}

.category-title {
    font-size: 1rem;
    font-weight: 600;
    margin-bottom: 0.5rem;
}

.category-count {
    font-weight: 400;
    color: var(--color-fg-darker, #888);
}

.roster-list {
    display: flex;
    flex-direction: column;
    gap: 0.3rem;
}

.roster-row {
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem 1rem;
    padding: 0.5rem 0.75rem;
    border-radius: var(--radius, 8px);
    background: var(--color-bg-lighter, #f5f5f5);
    align-items: baseline;
}

.roster-row--mine,
.roster-row--mine .roster-club {
    color: var(--color-accent);
}

.roster-seeking {
    font-size: 0.8rem;
    font-style: italic;
    color: var(--color-fg-darker, #888);
}

.roster-totals {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
    gap: 0.75rem;
    margin-bottom: 1.25rem;
}

.total-group {
    padding: 0.6rem 0.9rem;
    border: 1px solid var(--color-light-shadow, #ddd);
    border-radius: var(--radius, 8px);
}

.total-group h3 {
    margin: 0 0 0.35rem;
    font-size: 0.9rem;
}

.total-item {
    display: flex;
    justify-content: space-between;
    gap: 0.5rem;
    font-size: 0.85rem;
}

.total-item.big {
    font-size: 1rem;
}

.roster-name {
    font-weight: 600;
}

.roster-club {
    margin-left: auto;
    font-size: 0.85rem;
    color: var(--color-fg-darker, #888);
}
</style>
